import { useEffect, useState } from "react";
import { X, Copy, Check, RefreshCw, Key, CreditCard, Lock, ChevronDown, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { Language, dictionary } from "../../../utils/dictionary";
import { INTEGRATIONS_UI_CONFIG } from "../config/integrations.config";
import {
  generateConfioConnectionToken,
  getConfioIntegration,
} from "../../../services/confio/confioService";
import { handleApi } from "../../../utils/handleApi";

const CONFIO_LOGO = INTEGRATIONS_UI_CONFIG.confio.logoSrc;
const FLUXI_LOGO = "https://fluxi.s3.amazonaws.com/prod/assets/fluxi/fluxi-logo.svg";
const POLL_INTERVAL_MS = 5000;

interface ModalConfioIntegrationProps {
  setModalOpen: (open: boolean) => void;
  language: Language;
  onConnected?: () => void;
}

const ModalConfioIntegration = ({ setModalOpen, language, onConnected }: ModalConfioIntegrationProps) => {
  const [token, setToken] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    const checkConnection = async () => {
      const { res, error } = await handleApi(getConfioIntegration(), false);
      if (cancelled || error || !res) return;
      if (res.connected && res.active) {
        cancelled = true;
        clearInterval(interval);
        onConnected?.();
      }
    };

    const interval = setInterval(checkConnection, POLL_INTERVAL_MS);
    checkConnection();

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token, onConnected]);

  const handleGenerateToken = async () => {
    setLoading(true);
    const { res, error } = await handleApi(generateConfioConnectionToken(), false);
    setLoading(false);

    if (error || !res) {
      toast.error(dictionary["integration-error"][language]);
      return;
    }

    setToken(res.token);
    setCopied(false);
  };

  const handleCopy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      toast.success(dictionary["confio-token-copied"][language]);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(dictionary["integration-error"][language]);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[850px] overflow-hidden flex flex-col md:flex-row relative">
        <button
          onClick={() => setModalOpen(false)}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 md:hidden"
        >
          <X size={24} />
        </button>

        {/* Left Side - Form */}
        <div className="flex-1 p-6 md:p-8 flex flex-col">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 flex items-center justify-center shrink-0">
              <img src={CONFIO_LOGO} alt="Confío" className="w-full h-full object-contain rounded-full" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 leading-tight">
              {dictionary["confio-connect-title"][language]}
            </h2>
          </div>

          <p className="text-gray-600 mb-6 text-sm leading-relaxed">
            {dictionary["confio-connect-description"][language]}
          </p>

          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <span className="text-sm text-gray-700">
                {dictionary["confio-feature-methods"][language]}
              </span>
            </div>
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <span className="text-sm text-gray-700">
                {dictionary["confio-feature-secure"][language]}
              </span>
            </div>
            <div className="flex items-start gap-3">
              <RefreshCw className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <span className="text-sm text-gray-700">
                {dictionary["confio-feature-webhooks"][language]}
              </span>
            </div>
          </div>

          <div className="mt-auto pt-4 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {dictionary["confio-token-label"][language]}
            </label>

            {!token ? (
              <button
                type="button"
                onClick={handleGenerateToken}
                disabled={loading}
                className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Key className="w-4 h-4" />
                {loading
                  ? dictionary["loading"]?.[language] || "..."
                  : dictionary["confio-generate-token"][language]}
              </button>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={token}
                    readOnly
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                    className="w-full pl-10 pr-12 p-2.5 rounded-lg border border-gray-300 bg-gray-50 focus:ring-blue-100 focus:border-blue-500 focus:outline-none focus:ring-2 transition-all text-sm font-mono"
                  />
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title={dictionary["confio-copy-token"][language]}
                  >
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>

                <p className="text-[11px] text-gray-500 leading-relaxed">
                  {dictionary["confio-paste-instructions"][language]}
                </p>

                <div className="flex items-center gap-2 text-xs text-blue-600">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>{dictionary["confio-waiting-connection"][language]}</span>
                </div>

                <button
                  type="button"
                  onClick={handleGenerateToken}
                  disabled={loading}
                  className="text-xs text-blue-600 hover:text-blue-800 self-start disabled:opacity-50"
                >
                  {loading
                    ? dictionary["loading"]?.[language] || "..."
                    : dictionary["confio-regenerate-token"][language]}
                </button>
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5 px-4 rounded-lg border border-gray-200 transition-colors text-sm"
              >
                {dictionary["close"][language]}
              </button>
            </div>
          </div>
        </div>

        {/* Right Side - Visual */}
        <div className="hidden md:flex w-[200px] bg-gradient-to-b from-[#eff6ff] to-white relative items-center justify-center py-6 px-4 border-l border-gray-100 shrink-0">
          <button
            onClick={() => setModalOpen(false)}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex flex-col items-center justify-center gap-8 w-full h-full">
            <div className="bg-white rounded-[24px] shadow-[0_4px_20px_rgba(59,130,246,0.15)] border border-blue-100 p-5 w-24 h-24 flex items-center justify-center z-10 relative">
              <img src={FLUXI_LOGO} alt="Fluxi" className="w-full h-auto object-contain" />
            </div>

            <div className="flex flex-col items-center gap-1 text-blue-300">
              <ChevronDown strokeWidth={3} size={24} className="animate-[confioFade_1.5s_infinite_0ms]" />
              <ChevronDown strokeWidth={3} size={24} className="animate-[confioFade_1.5s_infinite_200ms]" />
              <ChevronDown strokeWidth={3} size={24} className="animate-[confioFade_1.5s_infinite_400ms]" />
            </div>

            <div className="bg-white rounded-[24px] shadow-sm border border-gray-200 p-3 w-24 h-24 flex items-center justify-center z-10 relative overflow-hidden">
              <img src={CONFIO_LOGO} alt="Confío" className="w-16 h-16 object-contain rounded-full" />
            </div>

            <style>{`
              @keyframes confioFade {
                0%, 100% { opacity: 0.3; transform: translateY(0); }
                50% { opacity: 1; transform: translateY(3px); }
              }
            `}</style>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalConfioIntegration;
